import { defineStore } from 'pinia'
import { useStoreAuth } from '@/stores/storeAuth'
import { useStoreProfile } from '@/stores/storeProfile'
import { useStoreTitlesUser } from '@/stores/storeTitlesUser'
import { useStoreLists } from '@/stores/storeLists'
import { supabase } from '@/supabase'
import { watchEffect } from 'vue'

// Module-level cache for on-demand title state checks (not persisted)
const _checkedTitleKeys = new Set()
const _pendingChecks = new Map()

export const useStoreTitleLogs = defineStore('storeTitleLogs', {
  persist: {
    pick: ['logs', 'loaded'],
    serializer: {
      serialize: JSON.stringify,
      deserialize: JSON.parse
    }
  },
  state: () => ({
    logs: [],
    loaded: false,
    loading: false,
    initReady: false,
    currentUserId: null,
    featureEnabled: false,
    _isSyncing: false // Prevent infinite sync loops
  }),
  actions: {
    // Activity per day for any profile, via the get_activity_calendar RPC:
    // seen-logs plus published reviews, with the day's average review score
    // when it had any. Returns [{ date: 'YYYY-MM-DD', count, avgScore }] for
    // WatchActivityHeatmap; not held in state - per-profile and cheap to
    // refetch.
    async fetchActivityCalendar(profileId, days = 365) {
      if (!profileId) return []

      try {
        const { data, error } = await supabase.rpc('get_activity_calendar', {
          p_profile_id: profileId,
          p_days: days
        })

        if (error) throw error

        return (data || []).map((row) => ({
          date: row.day,
          count: row.count,
          avgScore: row.avg_score
        }))
      } catch (error) {
        console.error('Failed to fetch activity calendar:', error.message)
        return []
      }
    },

    async init() {
      const storeAuth = useStoreAuth()
      const storeProfile = useStoreProfile()

      if (!storeAuth.user) {
        console.log('[storeTitleLogs] Skipping init - user not authenticated')
        return
      }

      // If user changed, clear old data
      if (this.currentUserId && this.currentUserId !== storeAuth.user.id) {
        this.logs = []
        this.loaded = false
        _checkedTitleKeys.clear()
        _pendingChecks.clear()
      }

      this.currentUserId = storeAuth.user.id
      this.featureEnabled = true
      this.initReady = true
      this.subscribeToChanges()

      // Always fetch fresh data to ensure titles relation is present
      await this.fetchRecentLogs()
    },

    subscribeToChanges() {
      supabase
        .channel('public:title_logs')
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'title_logs',
            filter: `profile_id=eq.${this.currentUserId}`
          },
          async (payload) => {
            console.log('[storeTitleLogs] INSERT', payload)
            // Realtime doesn't include joins, so fetch full data
            if (!payload.new.titles) {
              await this.fetchLogWithTitles(payload.new.id)
            } else {
              this.handleInsert(payload.new)
            }
          }
        )
        .on(
          'postgres_changes',
          {
            event: 'UPDATE',
            schema: 'public',
            table: 'title_logs',
            filter: `profile_id=eq.${this.currentUserId}`
          },
          async (payload) => {
            console.log('[storeTitleLogs] UPDATE', payload)
            // Check if the log already exists with titles data
            const existingLog = this.logs.find((l) => l.id === payload.new.id)
            if (existingLog?.titles) {
              // Preserve existing titles relation
              this.handleUpdate(payload.new)
            } else {
              // Fetch full data with titles
              await this.fetchLogWithTitles(payload.new.id)
            }
          }
        )
        .on(
          'postgres_changes',
          {
            event: 'DELETE',
            schema: 'public',
            table: 'title_logs',
            filter: `profile_id=eq.${this.currentUserId}`
          },
          (payload) => {
            console.log('[storeTitleLogs] DELETE', payload)
            this.handleDelete(payload.old.id)
          }
        )
        .subscribe()
    },

    handleInsert(log) {
      if (!log?.id) return
      // Check if already exists
      if (this.logs.some((l) => l.id === log.id)) return
      this.logs.push(log)
    },

    handleUpdate(log) {
      if (!log?.id) return
      const index = this.logs.findIndex((l) => l.id === log.id)
      if (index !== -1) {
        // Update existing log, preserving titles relation if new data doesn't have it
        const existingTitles = this.logs[index].titles
        this.logs[index] = {
          ...this.logs[index],
          ...log,
          titles: log.titles || existingTitles
        }
      } else {
        // New log, add it
        this.logs.push(log)
      }
    },

    // Fetch full log data with titles relation for realtime updates
    async fetchLogWithTitles(logId) {
      const { data, error } = await supabase
        .from('title_logs')
        .select('*, titles(id, tmdb_id, tmdb_type, title, tmdb_poster_path)')
        .eq('id', logId)
        .single()

      if (!error && data) {
        this.handleUpdate(data)
      }
      return data
    },

    handleDelete(logId) {
      this.logs = this.logs.filter((l) => l.id !== logId)
    },

    async fetchRecentLogs(limit = 100) {
      if (!this.initReady) {
        watchEffect(async () => {
          if (this.initReady) await this.fetchRecentLogs(limit)
        })
        return
      }

      this.loading = true
      try {
        const { data, error } = await supabase
          .from('title_logs')
          .select('*, titles(id, tmdb_id, tmdb_type, title, tmdb_poster_path)')
          .eq('profile_id', this.currentUserId)
          .is('deleted_at', null)
          .order('created_at', { ascending: false })
          .limit(limit)

        if (error) {
          console.error('[storeTitleLogs] Error fetching logs:', error)
        } else {
          const freshData = data || []
          const fetchedIds = new Set(freshData.map((l) => l.id))
          // Preserve on-demand logs (loaded by ensureTitleState) that
          // aren't in this bulk fetch — they may be outside the top N
          const preserved = this.logs.filter(
            (l) => !fetchedIds.has(l.id) && !l.deleted_at
          )
          this.logs = [...freshData, ...preserved]
          this.loaded = true
          // Mark all fetched titles as checked so we don't re-query them
          for (const log of freshData) {
            if (log.titles?.tmdb_id != null && log.titles?.tmdb_type) {
              _checkedTitleKeys.add(`${log.titles.tmdb_id}:${log.titles.tmdb_type}`)
            }
          }
        }
      } catch (err) {
        console.error('[storeTitleLogs] Fetch error:', err)
      } finally {
        this.loading = false
      }
    },

    // On-demand fetch for a specific title's logs by TMDB ID.
    // Returns immediately if already checked. Deduplicates in-flight requests.
    async ensureTitleState(tmdbId, tmdbType) {
      if (!this.initReady || !this.featureEnabled) return

      const key = `${tmdbId}:${tmdbType}`

      // Already checked (has logs in store, or confirmed empty)
      if (_checkedTitleKeys.has(key)) return

      // Already fetching this title
      if (_pendingChecks.has(key)) return _pendingChecks.get(key)

      const promise = (async () => {
        try {
          const { data, error } = await supabase
            .from('title_logs')
            .select(
              '*, titles!inner(id, tmdb_id, tmdb_type, title, tmdb_poster_path)'
            )
            .eq('profile_id', this.currentUserId)
            .eq('titles.tmdb_id', tmdbId)
            .eq('titles.tmdb_type', tmdbType)
            .is('deleted_at', null)

          if (!error && data) {
            if (data.length > 0) {
              data.forEach((log) => this.handleUpdate(log))
            } else {
              // No active logs — remove any stale persisted logs for this title
              this.logs = this.logs.filter(
                (l) =>
                  !(
                    l.titles?.tmdb_id === tmdbId &&
                    l.titles?.tmdb_type === tmdbType
                  )
              )
            }
          }
        } finally {
          _checkedTitleKeys.add(key)
          _pendingChecks.delete(key)
        }
      })()

      _pendingChecks.set(key, promise)
      return promise
    },

    async fetchLogsForTitle(titleId) {
      const { data, error } = await supabase
        .from('title_logs')
        .select('*, titles(id, tmdb_id, tmdb_type, title, tmdb_poster_path)')
        .eq('profile_id', this.currentUserId)
        .eq('title_id', titleId)
        .is('deleted_at', null)
        .order('created_at', { ascending: false })

      if (error) {
        console.error('[storeTitleLogs] Error fetching logs for title:', error)
        return []
      }

      // Merge with existing logs and mark as checked
      if (data) {
        data.forEach((log) => {
          this.handleUpdate(log)
          if (log.titles?.tmdb_id != null && log.titles?.tmdb_type) {
            _checkedTitleKeys.add(
              `${log.titles.tmdb_id}:${log.titles.tmdb_type}`
            )
          }
        })
      }
      return data || []
    },

    async createLog(title, state, options) {
      const opts = options || {}
      const { data, error } = await supabase.functions.invoke('title_logs', {
        body: {
          title,
          state,
          granularity: opts.granularity || 'movie',
          tv_season: opts.tvSeason || null,
          tv_episode: opts.tvEpisode || null,
          watched_on: opts.watchedOn || null,
          watched_service: opts.watchedService || null,
          watched_with_profiles: opts.watchedWithProfiles || [],
          watched_with_external: opts.watchedWithExternal || [],
          notes: opts.notes || null
        }
      })

      if (error) {
        console.error('[storeTitleLogs] Error creating log:', error)
        throw error
      }

      if (data?.log) {
        this.handleInsert(data.log)
      }
      return data
    },

    async transitionState(logId, newState) {
      const { data, error } = await supabase.functions.invoke(
        `title_logs/${logId}/state`,
        { body: { state: newState } }
      )

      if (error) {
        console.error('[storeTitleLogs] Error transitioning state:', error)
        throw error
      }

      if (data?.log) {
        this.handleUpdate(data.log)
      }
      return data
    },

    async updateLog(logId, updates) {
      const { data, error } = await supabase.functions.invoke(
        `title_logs/${logId}/update`,
        { body: updates }
      )

      if (error) {
        console.error('[storeTitleLogs] Error updating log:', error)
        throw error
      }

      if (data?.log) {
        this.handleUpdate(data.log)
      }
      return data
    },

    async deleteLog(logId) {
      const { data, error } = await supabase.functions.invoke(
        `title_logs/${logId}`,
        { method: 'DELETE' }
      )

      if (error) {
        console.error('[storeTitleLogs] Error deleting log:', error)
        throw error
      }

      // Remove from local state
      this.handleDelete(logId)
      return data
    },

    // Eagerly remove a title from the watchlist in storeLists.items
    // so the UI updates instantly without waiting for the realtime subscription
    _removeFromWatchlistList(tmdbId) {
      try {
        const storeLists = useStoreLists()
        const watchlist = storeLists.getSystemListByType('watchlist')
        if (!watchlist) return
        const item = storeLists.items.find(
          (i) =>
            i.list_id === watchlist.id &&
            i.titles?.tmdb_id == tmdbId
        )
        if (item) {
          storeLists.removeItem(item.id)
        }
      } catch (e) {
        // Non-critical, realtime subscription will handle it
      }
    },

    // Sync log state to legacy system (titles_user)
    async _syncToLegacy(title, logState) {
      // Prevent infinite sync loops
      if (this._isSyncing) return
      this._isSyncing = true

      const storeTitlesUser = useStoreTitlesUser()
      const titleData = {
        titleId: title.id,
        titleType: title.type
      }

      // Map log states to legacy flags
      // seen → is_seen=true
      // watchlist → is_watchlisted=true
      // in_progress → no direct legacy equivalent
      // Doc 27: is_watchlisted mirrors the existence of a watchlist intent
      // log, independent of the history state. Engaging normally consumes
      // the intent (the log is already deleted by the time we sync), so
      // only clear the flag when no intent log remains.

      // Skip feed events for all legacy syncs - the title_logs system
      // creates its own title_state_change feed items, so list_item_add/remove
      // from the legacy system would be duplicates
      const skipFeed = { skipFeedEvent: true }

      try {
        const hasIntentLog = this.isWatchlistedByTmdb(title.id, title.type)

        if (logState === 'seen') {
          await storeTitlesUser._setStatus('seen', true, titleData, skipFeed)
          if (!hasIntentLog) {
            await storeTitlesUser._setStatus('watchlist', false, titleData, skipFeed)
            this._removeFromWatchlistList(title.id)
          }
        } else if (logState === 'watchlist') {
          await storeTitlesUser._setStatus('watchlist', true, titleData, skipFeed)
        } else if (logState === 'in_progress') {
          if (!hasIntentLog) {
            await storeTitlesUser._setStatus('watchlist', false, titleData, skipFeed)
            this._removeFromWatchlistList(title.id)
          }
        }
      } catch (error) {
        console.error('[storeTitleLogs] Error syncing to legacy:', error)
      } finally {
        this._isSyncing = false
      }
    },

    // Clear a specific legacy flag
    async _clearLegacyState(title, legacyState) {
      // Prevent infinite sync loops
      if (this._isSyncing) return
      this._isSyncing = true

      const storeTitlesUser = useStoreTitlesUser()
      const titleData = {
        titleId: title.id,
        titleType: title.type
      }

      try {
        // Skip feed events - title_logs handles its own feed items
        await storeTitlesUser._setStatus(legacyState, false, titleData, {
          skipFeedEvent: true
        })
        if (legacyState === 'watchlist') {
          this._removeFromWatchlistList(title.id)
        }
      } catch (error) {
        console.error('[storeTitleLogs] Error clearing legacy state:', error)
      } finally {
        this._isSyncing = false
      }
    },

    // Helper: Clean up all logs for a title except one we want to keep/transition.
    // Deliberately includes the watchlist intent log - doc 27 decision 4,
    // engaging a title (seen/in_progress/dropped) consumes the intent.
    // Only the history-state paths call this; addToWatchlist never does.
    async _cleanupOtherLogs(titleId, keepLogId = null) {
      const logsToDelete = this.logs.filter(
        (l) => l.title_id === titleId && !l.deleted_at && l.id !== keepLogId
      )
      await Promise.all(logsToDelete.map((log) => this.deleteLog(log.id)))
      return logsToDelete.length
    },

    // Convenience methods for common actions
    async markAsSeen(title, options) {
      const opts = options || {}
      const titleId = await this._getTitleId(title)
      let result

      if (!titleId) {
        // Create new log directly
        result = await this.createLog(title, 'seen', opts)
      } else {
        const existingLog = this.getLatestLogForTitle(titleId)

        if (existingLog && existingLog.state !== 'seen') {
          // Clean up any OTHER logs before transitioning (prevents duplicates)
          await this._cleanupOtherLogs(titleId, existingLog.id)
          // Transition existing log
          result = await this.transitionState(existingLog.id, 'seen')
        } else {
          // Clean up all existing logs before creating new seen log (rewatch)
          await this._cleanupOtherLogs(titleId, null)
          result = await this.createLog(title, 'seen', opts)
        }
      }

      // Sync to legacy system (fire-and-forget, non-critical)
      this._syncToLegacy(title, 'seen')
      return result
    },

    async addToWatchlist(title, options) {
      const opts = options || {}

      // Doc 27: watchlist is an orthogonal intent record. It coexists with
      // any history log, so never clean up or transition other logs here.
      // The server create is idempotent - a 200 with the existing intent
      // log and a 201 with a new one are both success paths for invoke().
      const result = await this.createLog(title, 'watchlist', opts)

      // Sync to legacy system (fire-and-forget, non-critical)
      this._syncToLegacy(title, 'watchlist')
      return result
    },

    async startWatching(title, options) {
      const opts = options || {}
      const titleId = await this._getTitleId(title)
      let result

      if (!titleId) {
        result = await this.createLog(title, 'in_progress', opts)
      } else {
        const existingLog = this.getLatestLogForTitle(titleId)

        if (
          existingLog &&
          (existingLog.state === 'watchlist' || existingLog.state === 'seen')
        ) {
          // Clean up any OTHER logs before transitioning (prevents duplicates)
          await this._cleanupOtherLogs(titleId, existingLog.id)
          // Transition from watchlist/seen to in_progress
          result = await this.transitionState(existingLog.id, 'in_progress')
        } else {
          // Clean up all existing logs before creating new in_progress log
          await this._cleanupOtherLogs(titleId, null)
          result = await this.createLog(title, 'in_progress', opts)
        }
      }

      // Sync to legacy system (fire-and-forget, non-critical)
      this._syncToLegacy(title, 'in_progress')
      return result
    },

    async markAsDropped(title, options) {
      const opts = options || {}
      const titleId = await this._getTitleId(title)
      let result

      if (!titleId) {
        result = await this.createLog(title, 'dropped', opts)
      } else {
        const existingLog = this.getLatestLogForTitle(titleId)

        if (existingLog && existingLog.state !== 'dropped') {
          // Clean up any OTHER logs before transitioning (prevents duplicates)
          await this._cleanupOtherLogs(titleId, existingLog.id)
          // Transition existing log to dropped
          result = await this.transitionState(existingLog.id, 'dropped')
        } else {
          // Clean up all existing logs before creating new dropped log
          await this._cleanupOtherLogs(titleId, null)
          result = await this.createLog(title, 'dropped', opts)
        }
      }

      // Clear from legacy system (fire-and-forget, non-critical)
      this._clearLegacyState(title, 'watchlist')
      return result
    },

    async updateTVProgress(logId, season, episode) {
      return await this.updateLog(logId, {
        last_watched_season: season,
        last_watched_episode: episode,
        episodes_watched: {
          [`S${season}E${episode}`]: true
        }
      })
    },

    async removeFromState(title, state) {
      const titleId = await this._getTitleId(title)
      if (!titleId) return

      // Find ALL logs with this state (to handle duplicate entries)
      const logs = this.logs.filter(
        (l) => l.title_id === titleId && l.state === state && !l.deleted_at
      )

      // Delete all matching logs in parallel
      await Promise.all(logs.map((log) => this.deleteLog(log.id)))

      // Sync to legacy: clear the corresponding flag (fire-and-forget)
      // Always sync even if no logs found - the title may exist in the
      // list system without a corresponding log entry
      if (state === 'seen') {
        this._clearLegacyState(title, 'seen')
      } else if (state === 'watchlist') {
        this._clearLegacyState(title, 'watchlist')
      }
      // in_progress has no legacy equivalent to clear
    },

    // Clear ALL statuses for a title (handles duplicate state issues)
    async clearAllStatuses(title) {
      const titleId = await this._getTitleId(title)
      if (!titleId) return

      // Find ALL logs for this title
      const logs = this.logs.filter(
        (l) => l.title_id === titleId && !l.deleted_at
      )

      // Track which states we're clearing for legacy sync
      const statesToClear = new Set(logs.map((l) => l.state))

      // Delete all logs
      for (const log of logs) {
        await this.deleteLog(log.id)
      }

      // Sync to legacy: clear all corresponding flags
      if (statesToClear.has('seen')) {
        await this._clearLegacyState(title, 'seen')
      }
      if (statesToClear.has('watchlist')) {
        await this._clearLegacyState(title, 'watchlist')
      }
      // in_progress has no legacy equivalent

      return { clearedStates: [...statesToClear], logsDeleted: logs.length }
    },

    async migrateFromLegacy() {
      const { data, error } = await supabase.functions.invoke(
        'title_logs/migrate',
        { method: 'POST' }
      )

      if (error) {
        console.error('[storeTitleLogs] Migration failed:', error)
        throw error
      }

      // Refresh logs after migration
      await this.fetchRecentLogs()

      return data
    },

    async rollbackToLegacy() {
      const { data, error } = await supabase.functions.invoke(
        'title_logs/rollback',
        { method: 'POST' }
      )

      if (error) {
        console.error('[storeTitleLogs] Rollback failed:', error)
        throw error
      }

      // Clear local logs
      this.logs = []
      this.loaded = false
      this.featureEnabled = false

      return data
    },

    // Helper to get title_id from title object
    async _getTitleId(title) {
      // If it's already a UUID, return it
      if (
        typeof title === 'string' &&
        /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
          title
        )
      ) {
        return title
      }

      // Look up in local logs first
      const existingLog = this.logs.find(
        (l) =>
          l.titles?.tmdb_id === title.id && l.titles?.tmdb_type === title.type
      )

      if (existingLog) {
        return existingLog.title_id
      }

      // Query the database
      const { data } = await supabase
        .from('titles')
        .select('id')
        .eq('tmdb_id', title.id)
        .eq('tmdb_type', title.type)
        .single()

      return data?.id || null
    }
  },
  getters: {
    isFeatureEnabled: (state) => state.featureEnabled,

    getLogsForTitle: (state) => (titleId) => {
      return state.logs
        .filter((l) => l.title_id === titleId && !l.deleted_at)
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
    },

    getLatestLogForTitle: (state) => (titleId) => {
      const logs = state.logs.filter(
        (l) => l.title_id === titleId && !l.deleted_at
      )
      if (logs.length === 0) return null

      // Priority: seen/did-not-finish > in_progress > watchlist, then by date
      const stateOrder = { seen: 0, dropped: 0, in_progress: 1, watchlist: 2 }
      logs.sort((a, b) => {
        const aOrder = stateOrder[a.state] ?? 99
        const bOrder = stateOrder[b.state] ?? 99
        if (aOrder !== bOrder) {
          return aOrder - bOrder
        }
        return new Date(b.created_at) - new Date(a.created_at)
      })
      return logs[0]
    },

    getDerivedState: (state) => (titleId) => {
      const logs = state.logs.filter(
        (l) => l.title_id === titleId && !l.deleted_at
      )
      if (logs.length === 0) return null

      // Priority: seen > in_progress > watchlist
      if (logs.some((l) => l.state === 'seen')) return 'seen'
      if (logs.some((l) => l.state === 'in_progress')) return 'in_progress'
      if (logs.some((l) => l.state === 'watchlist')) return 'watchlist'
      return null
    },

    getDerivedStateByTmdb: (state) => (tmdbId, tmdbType) => {
      const logs = state.logs.filter(
        (l) =>
          l.titles?.tmdb_id === tmdbId &&
          l.titles?.tmdb_type === tmdbType &&
          !l.deleted_at
      )
      if (logs.length === 0) return null

      if (logs.some((l) => l.state === 'seen')) return 'seen'
      if (logs.some((l) => l.state === 'in_progress')) return 'in_progress'
      if (logs.some((l) => l.state === 'watchlist')) return 'watchlist'
      return null
    },

    // Doc 27: state of the active history log (seen | in_progress | dropped),
    // ignoring any watchlist intent log
    getHistoryStateByTmdb: (state) => (tmdbId, tmdbType) => {
      const logs = state.logs.filter(
        (l) =>
          l.titles?.tmdb_id === tmdbId &&
          l.titles?.tmdb_type === tmdbType &&
          l.state !== 'watchlist' &&
          !l.deleted_at
      )
      if (logs.length === 0) return null

      if (logs.some((l) => l.state === 'seen')) return 'seen'
      if (logs.some((l) => l.state === 'in_progress')) return 'in_progress'
      if (logs.some((l) => l.state === 'dropped')) return 'dropped'
      return null
    },

    // Doc 27: any active watchlist intent log, independent of history state
    isWatchlistedByTmdb: (state) => (tmdbId, tmdbType) => {
      return state.logs.some(
        (l) =>
          l.titles?.tmdb_id === tmdbId &&
          l.titles?.tmdb_type === tmdbType &&
          l.state === 'watchlist' &&
          !l.deleted_at
      )
    },

    getLatestLogByTmdb: (state) => (tmdbId, tmdbType) => {
      const logs = state.logs.filter(
        (l) =>
          l.titles?.tmdb_id === tmdbId &&
          l.titles?.tmdb_type === tmdbType &&
          !l.deleted_at
      )
      if (logs.length === 0) return null

      // Priority: seen > in_progress > watchlist, then by date
      const stateOrder = { seen: 0, in_progress: 1, watchlist: 2 }
      logs.sort((a, b) => {
        if (stateOrder[a.state] !== stateOrder[b.state]) {
          return stateOrder[a.state] - stateOrder[b.state]
        }
        return new Date(b.state_changed_at) - new Date(a.state_changed_at)
      })
      return logs[0]
    },

    getLogsByState: (state) => (logState) => {
      return state.logs
        .filter((l) => l.state === logState && !l.deleted_at)
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
    },

    watchlistLogs: (state) => {
      return state.logs
        .filter((l) => l.state === 'watchlist' && !l.deleted_at)
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
    },

    inProgressLogs: (state) => {
      return state.logs
        .filter((l) => l.state === 'in_progress' && !l.deleted_at)
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
    },

    seenLogs: (state) => {
      return state.logs
        .filter((l) => l.state === 'seen' && !l.deleted_at)
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
    },

    droppedLogs: (state) => {
      return state.logs
        .filter((l) => l.state === 'dropped' && !l.deleted_at)
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
    },

    getTVProgress: (state) => (titleId) => {
      const log = state.logs.find(
        (l) =>
          l.title_id === titleId &&
          l.granularity === 'tv_show' &&
          ['in_progress', 'seen'].includes(l.state) &&
          !l.deleted_at
      )

      if (!log) return null

      return {
        lastSeason: log.last_watched_season,
        lastEpisode: log.last_watched_episode,
        episodesWatched: log.episodes_watched || {},
        logId: log.id,
        state: log.state
      }
    },

    hasLogForTitle: (state) => (titleId) => {
      return state.logs.some((l) => l.title_id === titleId && !l.deleted_at)
    },

    hasLogForTmdb: (state) => (tmdbId, tmdbType) => {
      return state.logs.some(
        (l) =>
          l.titles?.tmdb_id === tmdbId &&
          l.titles?.tmdb_type === tmdbType &&
          !l.deleted_at
      )
    },

    // Get all unique active states for a title (to detect duplicates)
    getAllActiveStatesByTmdb: (state) => (tmdbId, tmdbType) => {
      const logs = state.logs.filter(
        (l) =>
          l.titles?.tmdb_id === tmdbId &&
          l.titles?.tmdb_type === tmdbType &&
          !l.deleted_at
      )
      // Return unique states
      return [...new Set(logs.map((l) => l.state))]
    },

    // Check if title has multiple different HISTORY states (indicates a data
    // issue). Doc 27: a history log plus a watchlist intent log is a legal
    // pair, not damage - only concurrent history states need repair.
    hasMultipleStatesByTmdb: (state) => (tmdbId, tmdbType) => {
      const logs = state.logs.filter(
        (l) =>
          l.titles?.tmdb_id === tmdbId &&
          l.titles?.tmdb_type === tmdbType &&
          l.state !== 'watchlist' &&
          !l.deleted_at
      )
      const uniqueStates = new Set(logs.map((l) => l.state))
      return uniqueStates.size > 1
    },

    // Get all logs for a title by TMDB (for cleanup operations)
    getAllLogsByTmdb: (state) => (tmdbId, tmdbType) => {
      return state.logs.filter(
        (l) =>
          l.titles?.tmdb_id === tmdbId &&
          l.titles?.tmdb_type === tmdbType &&
          !l.deleted_at
      )
    }
  }
})
